import React, { useState } from 'react';
import { Badge, Button, Divider, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { ChevronDown16Regular, DismissCircle20Regular, Filter20Filled, Filter20Regular } from '@fluentui/react-icons';

import PractitionerConsumptionItemFilterComponent from './PractitionerConsumption.Item.Filter.Component';
import './PractitionerConsumption.Filter.Styles.css';
import { IConsumptionsFilters } from '../../../../../models/Filters/ConsumptionsFilters';
import PractitionerFilterPanelComponent from './PractitionerFilterPanel/PraFilterPanelComponent';

// TODO: Replace with your actual filter state type
export interface IPractitionerConsumptionsFilters {
  FilterText: string;
  SelectedProtocols: any[];
  SelectedCurrency: string;
  SelectedStatus: any[];
  SelectedDateRange: [Date | undefined, Date | undefined];
}

interface Props {
  filterText: string;
  setFilterText: (text: string | undefined) => void;
  selectedProtocols: any[];
  setSelectedProtocols: (protocols: any[]) => void;
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  selectedStatus: any[];
  setSelectedStatus: (status: any[]) => void;
  selectedDateRange: [Date | undefined, Date | undefined];
  setSelectedDateRange: (dateRange: [Date | undefined, Date | undefined]) => void;
  onChangeFilters: (filters: IConsumptionsFilters) => void;
}

const PractitionerConsumptionFilterComponent = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  // controls the active-filters dropdown popover
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Compose a filter state object for panel
  const filterProps: IConsumptionsFilters = {
    FilterText: props.filterText,
    SelectedProtocols: props.selectedProtocols,
    SelectedCurrencys: props.selectedCurrency,
    SelectedStatus: props.selectedStatus,
    SelectedDateRange: props.selectedDateRange,
    SelectedConsumptions: [],
    SelectedProtocolPatients: [],
    SelectedContractConcepts: [],
    SelectedContractStages: [],
    SelectedPractitioners: [],
    SelectedSettlements: [],
    showGroupedByVisit: false,
    GroupId: undefined,
  };

  const onClearCriteria = (criteriaName: string) => {
    switch (criteriaName) {
      case 'Texto':
        props.setFilterText('');
        break;
      case 'Protocolos':
        props.setSelectedProtocols([]);
        break;
      case 'Monedas':
        props.setSelectedCurrency('');
        break;
      case 'Estado':
        props.setSelectedStatus([]);
        break;
      case 'Fecha desde':
        props.setSelectedDateRange([undefined, props.selectedDateRange[1]]);
        break;
      case 'Fecha hasta':
        props.setSelectedDateRange([props.selectedDateRange[0], undefined]);
        break;
      default:
        break;
    }
  };

  const onClearAll = () => {
    props.setFilterText('');
    props.setSelectedProtocols([]);
    props.setSelectedCurrency('');
    props.setSelectedStatus([]);
    props.setSelectedDateRange([undefined, undefined]);
  };

  // Only the criteria this view actually filters by
  const activeFilterCount = [
    !!props.filterText,
    (props.selectedProtocols?.length ?? 0) > 0,
    !!props.selectedCurrency,
    (props.selectedStatus?.length ?? 0) > 0,
    !!props.selectedDateRange?.[0],
    !!props.selectedDateRange?.[1],
  ].filter(Boolean).length;

  const filterBadge = (
    <Badge size="small" shape="circular" appearance="filled" color="danger" style={{ marginLeft: '0.4em' }}>
      {activeFilterCount}
    </Badge>
  );

  const Forms = isOpen && (
    <PractitionerFilterPanelComponent
      isOpen={isOpen}
      Filters={filterProps}
      onClose={() => setIsOpen(false)}
      onFilterChange={({
        FilterText,
        SelectedProtocols,
        SelectedCurrencys,
        SelectedStatus,
        SelectedDateRange
      }: IConsumptionsFilters) => {
        props.setFilterText(FilterText);
        props.setSelectedProtocols(SelectedProtocols);
        props.setSelectedCurrency(SelectedCurrencys);
        props.setSelectedStatus(SelectedStatus);
        props.setSelectedDateRange(SelectedDateRange);
      }}
    />
  );

  return (
    <div className="consumption-list-filter">
      <div className="filter-actions">
        {activeFilterCount > 0 ? (
          <div className="filter-split-button-container">
            <Button
              appearance="primary"
              shape="rounded"
              className="filter-split-main"
              icon={<Filter20Filled />}
              onClick={() => setIsOpen(true)}
            >
              Filtros {filterBadge}
            </Button>
            <Popover
              open={isDropdownOpen}
              onOpenChange={(_, data) => setIsDropdownOpen(data.open)}
              positioning="below-end"
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  appearance="primary"
                  shape="rounded"
                  className="filter-split-chevron"
                  icon={<ChevronDown16Regular />}
                />
              </PopoverTrigger>
              <PopoverSurface className="filter-dropdown-surface">
                <div className="filter-dropdown-chips">
                  {props.filterText && (
                    <PractitionerConsumptionItemFilterComponent
                      criteriaName="Texto"
                      criteria={[props.filterText]}
                      onClearCriteria={onClearCriteria}
                    />
                  )}
                  {props.selectedProtocols?.length > 0 && (
                    <PractitionerConsumptionItemFilterComponent
                      criteriaName="Protocolos"
                      criteria={props.selectedProtocols.map((p) => p.title || p.id)}
                      onClearCriteria={onClearCriteria}
                    />
                  )}
                  {props.selectedCurrency && (
                    <PractitionerConsumptionItemFilterComponent
                      criteriaName="Monedas"
                      criteria={[props.selectedCurrency]}
                      onClearCriteria={onClearCriteria}
                    />
                  )}
                  {props.selectedStatus?.length > 0 && (
                    <PractitionerConsumptionItemFilterComponent
                      criteriaName="Estado"
                      criteria={props.selectedStatus.map((s) => s.Name || s.id)}
                      onClearCriteria={onClearCriteria}
                    />
                  )}
                  {props.selectedDateRange?.[0] && (
                    <PractitionerConsumptionItemFilterComponent
                      criteriaName="Fecha desde"
                      criteria={[props.selectedDateRange[0].toLocaleDateString()]}
                      onClearCriteria={onClearCriteria}
                    />
                  )}
                  {props.selectedDateRange?.[1] && (
                    <PractitionerConsumptionItemFilterComponent
                      criteriaName="Fecha hasta"
                      criteria={[props.selectedDateRange[1].toLocaleDateString()]}
                      onClearCriteria={onClearCriteria}
                    />
                  )}
                </div>
                <Divider className="filter-dropdown-divider" />
                <div className="filter-dropdown-footer">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissCircle20Regular />}
                    onClick={() => { onClearAll(); setIsDropdownOpen(false); }}
                  >
                    Limpiar todos
                  </Button>
                </div>
              </PopoverSurface>
            </Popover>
          </div>
        ) : (
          <Button
            appearance="outline"
            shape="rounded"
            icon={<Filter20Regular />}
            onClick={() => setIsOpen(true)}
          >
            Filtros
          </Button>
        )}
      </div>
      {Forms}
    </div>
  );
};

export default PractitionerConsumptionFilterComponent;
